// typescript
import React from "react";

import {suggestEmailCorrection} from "@/main/validation/emailTypo";

export type EmailTypoSuggestionController = {
    /** The current high-confidence correction, or null when there is nothing to suggest. */
    suggestion: string | null;
    /**
     * Re-evaluate for a value (call on blur, or as a submit/continue gate).
     * Updates the visible suggestion and returns it, so callers can decide
     * whether to hold and surface the nudge before proceeding. Respects
     * per-value dismissals.
     */
    evaluate: (value: string) => string | null;
    /** Hide the current suggestion without suppressing it (call while the value is being edited). */
    clear: () => void;
    /** Dismiss and suppress any future suggestion for this exact value ("Keep mine"). */
    dismiss: (value: string) => void;
};

/**
 * Owns the email typo-suggestion lifecycle for a single email field:
 * blur-triggered evaluation, clear-on-edit, and per-value dismissal.
 *
 * Calibration lives here: we only ever surface a suggestion after an explicit
 * evaluate() (wired to blur), never on every keystroke, and once the user says
 * "Keep mine" for a value we never nag them about that exact value again.
 *
 * Design: designs/04-live/2026-06-10-email-typo-nudge-ux-design.md
 */
export function useEmailTypoSuggestion(): EmailTypoSuggestionController {
    const [suggestion, setSuggestion] = React.useState<string | null>(null);
    const dismissedRef = React.useRef<Set<string>>(new Set());

    const evaluate = React.useCallback((value: string): string | null => {
        const normalized = (value ?? "").trim();
        if (!normalized || dismissedRef.current.has(normalized)) {
            setSuggestion(null);
            return null;
        }
        const next = suggestEmailCorrection(normalized)?.suggestion ?? null;
        setSuggestion(next);
        return next;
    }, []);

    const clear = React.useCallback(() => {
        setSuggestion(null);
    }, []);

    const dismiss = React.useCallback((value: string) => {
        const normalized = (value ?? "").trim();
        if (normalized) {
            dismissedRef.current.add(normalized);
        }
        setSuggestion(null);
    }, []);

    return {suggestion, evaluate, clear, dismiss};
}
